import { debounce, findLast, get, isEqual, merge, reduce } from 'lodash';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { system } from 'styled-system';

import { useTheme } from '@hivebrite/design-system/Theme';

import { getBpNum } from 'hb-react/shared/components/DesignSystem/Grid/utils/getBpNum';
import mergeRefs from 'hb-react/shared/utils/mergeRefs';
import isTestEnvironment from 'helpers/isTestEnvironment';

import type {
  GridContext,
  MatchMedia,
  UseGridProps,
  UseGridReturns,
} from '../types';

export const useGrid = ({
  columnsInMobile,
  displayGrid,
  inputRef,
  overrideColGap,
  overrideNumCols,
  overrideRowGap,
}: UseGridProps): UseGridReturns => {
  const theme = useTheme();

  const colGap = useMemo(
    () => overrideColGap ?? theme.grid.colGap,
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [overrideColGap],
  );

  const numCols = useMemo(
    () => overrideNumCols ?? theme.grid.numCols,
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [overrideNumCols],
  );

  const rowGap = useMemo(
    () => overrideRowGap ?? theme.grid.rowGap,
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [overrideRowGap],
  );

  const gridRef = useRef<HTMLDivElement>(null);

  /* Use the default (`_`: mobile) value if object or the just the value */
  const defaultGridState = useMemo<GridContext>(
    () => ({
      colGap: getBpNum(colGap, '_'),
      columnsInMobile: Boolean(columnsInMobile),
      gridWidth: gridRef.current?.clientWidth ?? 0,
      numCols: getBpNum(numCols, '_'),
      rowGap: get(rowGap, '_', rowGap),
    }),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [colGap, columnsInMobile, numCols, gridRef.current, rowGap],
  );

  const [gridState, setGridState] = useState<GridContext>(defaultGridState);

  const queries = useMemo(
    () =>
      system({
        colGap: true,
        numCols: true,
        rowGap: true,
      })({
        colGap,
        numCols,
        rowGap,
        theme,
      }),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [colGap, numCols, rowGap],
  );

  const matchMedias = useMemo(
    () =>
      reduce(
        queries,
        (result, _, query) =>
          /@media/.test(query)
            ? result.concat({
                matchMedia: window.matchMedia(query.replace('@media ', '')),
                query,
              })
            : result,
        [] as MatchMedia[],
      ),
    [queries],
  );

  /* useLayoutEffect instead of useEffect to avoid extra re-render */
  useLayoutEffect(() => {
    const handleResize = () => {
      /* Find largest breakpoint that matches, right to left */
      const matches = findLast(
        matchMedias,
        ({ matchMedia }) => matchMedia.matches,
      );

      setGridState((prevState) => {
        const newState = matches
          ? merge({}, defaultGridState, queries[matches.query], {
              gridWidth: gridRef.current?.clientWidth,
            })
          : null;

        return isEqual(newState, prevState) ? prevState : newState;
      });
    };

    const handleResizeDebounced = debounce(handleResize, 100);

    /* Update on first render and when `matches` changes */
    handleResize();

    window.addEventListener('resize', handleResizeDebounced);

    return () => {
      window.removeEventListener('resize', handleResizeDebounced);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [matchMedias]);

  const [displayGridHelper, setDisplayGridHelper] = useState(
    Boolean(displayGrid),
  );

  useEffect(() => {
    setDisplayGridHelper(Boolean(displayGrid));
  }, [displayGrid]);

  useEffect(() => {
    /* Display GridHelper on shift-cmd/meta-G or shift-ctrl-G */
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G') {
        e.preventDefault();

        setDisplayGridHelper((prevState) => !prevState);
      }
    };

    if (isTestEnvironment) {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      if (isTestEnvironment) {
        document.removeEventListener('keydown', handleKeyDown, true);
      }
    };
  }, []);

  return {
    displayGridHelper,
    gridContextValue: gridState ?? defaultGridState,
    ref: mergeRefs([inputRef, gridRef]),
  };
};
